// src/pages/Blog.js
import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import BlogPost from '../components/BlogPost'; // Adjust the path as necessary

const BlogContainer = styled.div`
    padding: 4rem 2rem;
    max-width: 1200px;
    margin: auto;
    background-color: ${props => props.theme.colors.background};
`;

const HeroSection = styled.div`
    background: linear-gradient(5deg, 
        ${props => props.theme.colors.primary}, 
        ${props => props.theme.colors.secondary}
    );
    padding: ${props => props.theme.spacing.xxl} 0;
    color: white;
    text-align: center;
    min-height: 30vh; /* Adjust height as needed */
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BlogHeader = styled(motion.h1)`
    color: white; /* Change to white for visibility on gradient */
    font-size: 3rem;
`;

const Blog = () => {
   const blogPosts = [
      {
         title: "To What Extent Should We Use AI in Healthcare?",
         meta: "Published on December 10, 2024 | By Elva Osorio",
         content: `
         <p>As a tech consultant, I’ve closely followed the integration of AI in healthcare, especially its transformative potential and the ethical dilemmas it raises. The UnitedHealthcare scandal has underscored the critical need for responsible AI implementation, reminding us that technological innovation without ethics can cause profound harm.</p>

         <p>Predictive analytics is among AI’s most promising healthcare applications, capable of revolutionizing patient care by forecasting disease progression, tailoring treatment plans, and improving recovery outcomes. However, the UnitedHealthcare case serves as a sobering example of what happens when profits overshadow purpose. Deploying an AI model with a shocking 90% error rate to evaluate insurance claims wasn’t just a technical blunder—it was a moral failure. Patients became mere data points in a profit-driven algorithm, violating Kant’s principle to treat humanity as an end, never merely as a means.</p>
         
         <p>Philosopher Peter Singer’s call to expand our moral circle is particularly relevant here. Decisions about healthcare AI must prioritize the most vulnerable, considering the far-reaching impact on lives and trust. UnitedHealthcare’s choices betrayed that responsibility, turning ethical oversight into an afterthought. Each wrongful denial represents a person in need, a family in turmoil—a stark reminder that in healthcare, data is never abstract; it’s human.</p>
         
         <p>The pursuit of innovation must always align with empathy. As we advance healthcare AI, we need transparency, rigorous testing, and constant human oversight. Recklessly deploying unproven technology in critical sectors, as Du Châtelet warns, ignores the uncertainties we navigate daily. While AI can enhance decision-making, it must never replace human judgment in matters of life and health.</p>
         
         <p>The UnitedHealthcare debacle should inspire us to act with wisdom and compassion. The future of healthcare AI is bright, but only if we learn from these failures. Our commitment must be to ethical practices that safeguard humanity, ensuring technology serves people—not the other way around.</p>
`
      },
      {
         title: "Friday Night FOMO? Smart Contracts Have Your Back",
         meta: "Published on December 6, 2024 | By Elva Osorio",
         content: `
         <p>Picture this: It’s Friday night, and the Brooklyn Mirage is calling. Your friends are already hyping up the night, but there’s a problem—you’re the only one without a ticket. Desperate, you find someone on Reddit offering a spare, but there’s a catch: they want you to Venmo them first, promising to send the ticket afterward. Now you’re stuck—trust them and risk getting scammed, or miss out entirely?</p>

         <p>What if there was a way to guarantee fairness in this transaction? Enter smart contracts. These digital agreements ensure both sides fulfill their promises before anything is exchanged. Think of it as your personal digital middleman—but without the sketchy vibes.</p>
       
         <p>Smart contracts operate on blockchain platforms like Ethereum, using code to execute terms automatically. In our ticket dilemma, a smart contract would hold your payment in escrow, releasing it only when the ticket is transferred to you. No trust required—just tech that works.</p>
       
         <p>This technology isn’t just about fixing last-minute FOMO. It’s poised to redefine industries. Philosopher Jean-Jacques Rousseau wrote about the social contract as the foundation of society, built on mutual trust. In a world where trust is scarce, smart contracts offer a way to rebuild it digitally, ensuring fairness and transparency in every interaction.</p>
       
         <p>Ethereum leads this revolution with its ability to power decentralized applications (dApps). Unlike Bitcoin’s singular focus on digital currency, Ethereum creates an ecosystem where smart contracts thrive. Real estate deals, freelance payments, insurance claims—processes that traditionally relied on middlemen—are becoming faster, cheaper, and more secure.</p>
       
         <p>But with great power comes responsibility. Poorly written code or bad actors could exploit this technology, reminding us of philosopher Hannah Arendt’s warning about uncritical reliance on tools. As we embrace smart contracts, we must balance innovation with ethical considerations to avoid repeating the mistakes of the past.</p>
       
         <p>The potential of smart contracts is immense, promising a future where trust is no longer a scarce resource but a built-in feature of our digital lives. Whether it’s securing your Friday night plans or reshaping global commerce, this technology is just getting started. The next time you’re faced with a risky transaction, imagine a world where smart contracts eliminate the guesswork—and the stress.</p>
         `
       }
   ];

   return (
      <BlogContainer>
         <HeroSection>
            <BlogHeader
               initial={{ opacity: .5, y:-50 }}
               animate={{ opacity:.9, y :0 }}
               transition={{ duration:.5 }}
            >
               Tech Insights
            </BlogHeader>
         </HeroSection>
         {blogPosts.map((post, index) => (
            <BlogPost key={index} {...post} />
         ))}
      </BlogContainer>
   );
};

export default Blog;

